import { useQueryClient } from "@tanstack/react-query";
import { Check, DotsVertical, MessageTextSquare02, XClose } from "@untitledui/icons";
import { useState } from "react";
import { useBlockCreator } from "@/api/blocklist";
import type { TargetCollaborationCreator } from "@/api/collaborations";
import { useApproveSampleRequest, useDenySampleRequest } from "@/api/collaborations";
import { GroupedActionMenu } from "@/components/action-menu/grouped-action-menu";
import { InviteToCollaborationModal } from "@/components/assurance/invite-to-collaboration-modal";
import { Button } from "@/components/base/buttons/button";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { MessageCreatorsDialog } from "@/components/message-creator-dialogs";
import { RemoveCreatorFromCollabModal } from "@/components/remove-creator-from-collab-modal";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { buildCreatorActionGroups } from "@/lib/creator-action-groups";
import { BulkCohortModal } from "@/pages/creator-cohorts/bulk-cohort-modal";
import { AddtoListsModal } from "@/pages/creator-lists/add-to-lists-modal";
import { useMerchant } from "@/providers/MerchantProvider";
import { success, error as toastError } from "@/providers/ToastProvider";

import type { CreatorStatusTab } from "./collaboration-table-columns";

type CreatorRowActionsProps = {
  creator: TargetCollaborationCreator;
  collaborationId: string;
  collaborationName: string;
  tab?: CreatorStatusTab;
};

type DialogType = "message" | "remove" | "invite" | "addToList" | "addToCohort" | null;

export const CreatorRowActions = ({ creator, collaborationId, collaborationName, tab }: CreatorRowActionsProps) => {
  const queryClient = useQueryClient();
  const merchantId = useMerchant();

  const [openDialog, setOpenDialog] = useState<DialogType>(null);

  const { mutateAsync: approveSampleRequest, isPending: isApproving } = useApproveSampleRequest();
  const { mutateAsync: denySampleRequest, isPending: isDenying } = useDenySampleRequest();
  const blockCreator = useBlockCreator();

  const closeDialog = () => setOpenDialog(null);

  const invalidateCollaborationDetail = () => {
    queryClient.invalidateQueries({
      queryKey: ["target-collaboration-detail", merchantId, collaborationId],
    });
    queryClient.invalidateQueries({
      queryKey: ["open-collaboration-detail", merchantId, collaborationId],
    });
  };

  const handleOpenDialog = (dialog: DialogType) => () => setOpenDialog(dialog);

  const handleApprove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!creator.pendingSampleApplicationId) return;
    await approveSampleRequest({ collaborationId, applicationId: creator.pendingSampleApplicationId });
    success(`Approved sample request for ${creator.nickname ?? creator.username}`);
  };

  const handleDeny = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!creator.pendingSampleApplicationId) return;
    await denySampleRequest({ collaborationId, applicationId: creator.pendingSampleApplicationId });
    success(`Rejected sample request for ${creator.nickname ?? creator.username}`);
  };

  const handleBlock = async () => {
    if (!creator.tiktokCreatorOpenId) return;
    try {
      await blockCreator.mutateAsync(creator.tiktokCreatorOpenId);
      success(`Blocked ${creator.nickname ?? creator.username ?? "creator"}. Unblock anytime from Blocked Creators.`);
    } catch {
      toastError("Failed to block creator");
    }
  };

  const isSampleTab = tab === "sample_requested";

  const actionGroups = buildCreatorActionGroups({
    onInvite: handleOpenDialog("invite"),
    onMessage: handleOpenDialog("message"),
    onAddToList: handleOpenDialog("addToList"),
    onAddToCohort: handleOpenDialog("addToCohort"),
    onBlock: handleBlock,
    onRemove: handleOpenDialog("remove"),
    cohortsEnabled: !!creator.creatorId,
    blocklistEnabled: !!creator.tiktokCreatorOpenId,
  }).map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      if (item.key === "message" || item.key === "add-to-list" || item.key === "add-to-cohort") {
        return !!creator.creatorId;
      }
      if (item.key === "remove") return !!creator.creatorId && !isSampleTab && tab !== "removed";
      return true;
    }),
  }));

  return (
    <>
      <div className="flex items-center justify-end">
        {isSampleTab ? (
          <>
            <Tooltip title="Approve request">
              <Button
                color="tertiary"
                size="sm"
                iconLeading={<Check className="w-4 h-4" />}
                aria-label="Approve request"
                isDisabled={!creator.pendingSampleApplicationId || isApproving || isDenying}
                onClick={handleApprove}
              />
            </Tooltip>
            <Tooltip title="Reject request">
              <Button
                color="tertiary"
                size="sm"
                iconLeading={<XClose className="w-4 h-4" />}
                aria-label="Reject request"
                isDisabled={!creator.pendingSampleApplicationId || isApproving || isDenying}
                onClick={handleDeny}
              />
            </Tooltip>
          </>
        ) : (
          creator.creatorId && (
            <Tooltip title="Message creator">
              <Button
                color="tertiary"
                size="sm"
                iconLeading={<MessageTextSquare02 className="w-4 h-4" />}
                aria-label="Message creator"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setOpenDialog("message");
                }}
              />
            </Tooltip>
          )
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center justify-center w-8 h-8 hover:bg-secondary rounded transition-colors"
              onClick={(e) => e.stopPropagation()}
              aria-label="More actions"
            >
              <DotsVertical className="w-4 h-4 text-tertiary" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <GroupedActionMenu groups={actionGroups} />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {creator.creatorId && openDialog === "message" && (
        <MessageCreatorsDialog
          recipients={[
            {
              creatorId: creator.creatorId,
              handle: creator.nickname ?? creator.username ?? null,
              thumbnail: creator.avatar,
            },
          ]}
          open={openDialog === "message"}
          onOpenChange={(open) => {
            if (!open) closeDialog();
          }}
        />
      )}

      {creator.creatorId && openDialog === "remove" && (
        <RemoveCreatorFromCollabModal
          creator={{
            creatorId: creator.creatorId,
            creatorName: creator.nickname ?? creator.username,
            thumbnail: creator.avatar,
            targetCollaborations: [
              {
                collaborationId,
                name: collaborationName,
                status: creator.status,
              },
            ],
          }}
          open={openDialog === "remove"}
          onOpenChange={(open) => {
            if (!open) closeDialog();
          }}
          onSuccess={invalidateCollaborationDetail}
        />
      )}

      {openDialog === "invite" && (
        <InviteToCollaborationModal
          creator={{
            openId: creator.tiktokCreatorOpenId,
            username: creator.username,
            nickname: creator.nickname,
            avatar: creator.avatar,
          }}
          excludeCollaborationId={collaborationId}
          open={openDialog === "invite"}
          onOpenChange={(open) => {
            if (!open) closeDialog();
          }}
          onSuccess={invalidateCollaborationDetail}
        />
      )}

      {creator.creatorId && openDialog === "addToList" && (
        <AddtoListsModal
          selectedCreators={[creator.creatorId]}
          onAddToList={closeDialog}
          open={openDialog === "addToList"}
          onOpenChange={(open) => {
            if (!open) closeDialog();
          }}
        />
      )}

      {creator.creatorId && openDialog === "addToCohort" && (
        <BulkCohortModal
          creatorIds={[creator.creatorId]}
          open={openDialog === "addToCohort"}
          onOpenChange={(open) => {
            if (!open) closeDialog();
          }}
          onApplied={closeDialog}
        />
      )}
    </>
  );
};
